import { call } from 'redux-saga/effects'
import { APIS } from '../../_config'
import { CallApi } from '../api/callApi'
import { ErrorResponse, SuccessResponse } from '../api/apiTypes'
import { trackError } from '../../utils/analytics/rumTracking'

type ChatToExperimentAction<P, R> = { type: string; payload: P; onSuccess: (response: SuccessResponse<R>) => void; onError: (response: ErrorResponse<R>) => void }

// Shared by every saga below: run the API call effect, resolve the action's callbacks, and report failures.
// The effect is built inside the try so a malformed payload is reported through onError, as each saga did before.
function* runCall<P, R>(sagaName: string, defaultErrorMessage: string, action: ChatToExperimentAction<P, R>, buildEffect: () => any): any {
    try {
        const resp = yield buildEffect()
        if (resp.status >= 200 && resp.status < 300 && resp.data) {
            action?.onSuccess(resp.data as SuccessResponse<R>)
        } else {
            action?.onError(resp.data as ErrorResponse<R>)
        }
    } catch (e: any) {
        action?.onError({
            message: e?.message || defaultErrorMessage,
            status_code: 400,
            success: false,
        })
        trackError(e instanceof Error ? e : new Error(e?.message || 'Saga error'), { saga: sagaName, action: action?.type })
    }
}

export function* createSession<P, R>(action: ChatToExperimentAction<P, R>): any {
    yield* runCall('createSession', 'Failed to create agent session', action, () => call(CallApi.POST, APIS.CHAT_TO_EXPERIMENT.CREATE_SESSION, action.payload, true))
}

export function* chat<P, R>(action: ChatToExperimentAction<P, R>): any {
    yield* runCall('chat', 'Failed to chat with agent', action, () => {
        const { session_id, ...data } = action.payload as { session_id: string; [key: string]: any }
        return call(CallApi.POST, APIS.CHAT_TO_EXPERIMENT.CHAT(session_id), data, true)
    })
}

export function* listSessions<P, R>(action: ChatToExperimentAction<P, R>): any {
    yield* runCall('listSessions', 'Failed to list sessions', action, () => call(CallApi.GET, APIS.CHAT_TO_EXPERIMENT.LIST_SESSIONS, action.payload, false, true))
}

export function* getSessionMessages<P, R>(action: ChatToExperimentAction<P, R>): any {
    yield* runCall('getSessionMessages', 'Failed to get session messages', action, () => {
        const { session_id } = action.payload as { session_id: string }
        return call(CallApi.GET, APIS.CHAT_TO_EXPERIMENT.GET_SESSION_MESSAGES(session_id), {}, false, true)
    })
}

export function* deleteSession<P, R>(action: ChatToExperimentAction<P, R>): any {
    yield* runCall('deleteSession', 'Failed to delete session', action, () => {
        const { session_id } = action.payload as { session_id: string }
        return call(CallApi.DELETE, APIS.CHAT_TO_EXPERIMENT.DELETE_SESSION(session_id), {}, true)
    })
}

export function* chatStepsStream<P, R>(action: ChatToExperimentAction<P, R>): any {
    yield* runCall('chatStepsStream', 'Failed to stream chat steps', action, () => call(CallApi.GET, APIS.CHAT_TO_EXPERIMENT.CHAT_STEPS_STREAM, action.payload, true))
}

export function* getSupportNote<P, R>(action: ChatToExperimentAction<P, R>): any {
    yield* runCall('getSupportNote', 'Failed to get support note', action, () => call(CallApi.GET, APIS.CHAT_TO_EXPERIMENT.GET_SUPPORT_NOTE, action.payload, false, true))
}

export function* createFilterSession<P, R>(action: ChatToExperimentAction<P, R>): any {
    yield* runCall('createFilterSession', 'Failed to create filter session', action, () => call(CallApi.POST, APIS.CHAT_TO_EXPERIMENT.CREATE_FILTER_SESSION, action.payload, true))
}

// Separate from `chat` (takeLatest) so a filter-tab message never cancels, or is cancelled by, the main chat widget.
export function* filterChat<P, R>(action: ChatToExperimentAction<P, R>): any {
    yield* runCall('filterChat', 'Failed to chat with filter assistant', action, () => {
        const { session_id, ...data } = action.payload as { session_id: string; [key: string]: any }
        return call(CallApi.POST, APIS.CHAT_TO_EXPERIMENT.CHAT(session_id), data, true)
    })
}
